import React, { useCallback, useContext, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useSaveUnsavedChangesInForm } from "src/hooks/save-unsaved-changes-in-form/useSaveUnsavedChangesInForm";
import { FORM_STATUS } from "src/utils/Form";
import { useSaveFormByShortcut } from "src/hooks/form/useSaveFormByShortcut";
import { LOADING_TYPE } from "src/stores/krediflow/InvoiceEnums";

import styles from "./InvoiceForm.module.css";

import { InvoiceFormDetails } from "./details/InvoiceFormDetails";

export enum ERROR_TYPE {
    NONE = 0,
    REALESTATE = 1
}

interface InvoiceFormProps {
    displaySaveAndDiscardButtonRow: boolean;
}

const InvoiceFormBase = (props: InvoiceFormProps) => {
    const { t } = useTranslation();
    const { navStore, uiStore, invoiceStore, invoiceFormStore } = useContext(RootStoreContext);

    const invoiceFormContainerRef = useRef<HTMLDivElement>(null);
    const invoiceFormRef = useRef<HTMLDivElement>(null);

    const { displaySaveAndDiscardButtonRow } = props;

    const screenSize =
        uiStore.currentScreenSize <= SCREEN_SIZE.MOBILE_L ? uiStore.currentScreenSize : uiStore.invoicePaneScreenSize;

    useEffect(() => {
        navStore.setTitle("");
        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
    }, [navStore]);

    // Use ResizeObserver to observe the height of the InvoiceForm container
    useEffect(() => {
        const invoiceFormContainerElement = invoiceFormContainerRef.current;
        const invoiceFormElement = invoiceFormRef.current;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === invoiceFormContainerElement) {
                    let newContainerHeight = entry.contentRect.height;

                    uiStore.setInvoiceFormContainerHeight(newContainerHeight);
                }

                if (entry.target === invoiceFormElement) {
                    uiStore.setInvoiceFormHeight(entry.contentRect.height);
                }
            }
        });

        if (invoiceFormContainerElement) {
            resizeObserver.observe(invoiceFormContainerElement);
        }

        if (invoiceFormElement) {
            resizeObserver.observe(invoiceFormElement);
        }

        return () => {
            if (invoiceFormContainerElement) {
                resizeObserver.unobserve(invoiceFormContainerElement);
            }

            if (invoiceFormElement) {
                resizeObserver.unobserve(invoiceFormElement);
            }
        };
    }, [uiStore, invoiceStore.isEditing]);

    const { saveFormModal, closeSaveFormModalAndGoBack } = useSaveUnsavedChangesInForm({
        saveFormFunction:
            invoiceStore.currentInvoice?.isNewInvoice && !invoiceStore.currentInvoice.isFileSelected
                ? invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal
                : invoiceFormStore.saveForm(true),
        currentFormStatus:
            invoiceStore.loadingType === LOADING_TYPE.SAVING_INVOICE ? FORM_STATUS.SAVING_FORM : FORM_STATUS.NONE,
        isFormDirty: invoiceStore.currentInvoice?.hasChanges ?? false
    });

    useSaveFormByShortcut({
        saveFormFunction: invoiceFormStore.saveForm(false),
        isFormDirty: invoiceStore.currentInvoice?.hasChanges ?? false
    });

    const onResetForm = useCallback(async () => {
        await invoiceFormStore.resetForm();
    }, [invoiceFormStore]);

    invoiceStore.setUserDefinedAfterSaveFunction(closeSaveFormModalAndGoBack);

    const saveFormButtonLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.FromSaveButtonSpinner} ${
                    invoiceStore.loadingType !== LOADING_TYPE.SAVING_INVOICE ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const saveInvoiceWithoutDocumentModal = (
        <UI.Modal
            show={invoiceFormStore.isSaveInvoiceWithoutDocumentModalDisplayed}
            onClose={invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal}
        >
            <div>
                <div>
                    <div>{t("screens.kredi_flow.saving_without_document_warning")}</div>
                </div>
                <div>
                    <UI.Button
                        className="my-2"
                        label={t("screens.kredi_flow.confirm_saving_without_document")}
                        onClick={invoiceFormStore.saveForm(true)}
                    />
                    <UI.Button
                        className="FormSaveButtonCancel"
                        label={t("labels.cancel")}
                        onClick={invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal}
                    />
                </div>
            </div>
        </UI.Modal>
    );

    const saveButton = (
        <UI.Button
            onClick={
                invoiceStore.currentInvoice?.isNewInvoice && !invoiceStore.currentInvoice.isFileSelected
                    ? invoiceFormStore.toggleDisplaySaveInvoiceWithoutDocumentModal
                    : invoiceFormStore.saveForm(true)
            }
            className={screenSize <= SCREEN_SIZE.MOBILE_L ? "ticket-button mx-2" : styles.SaveButton}
            label={saveFormButtonLabel}
            disabled={invoiceStore.loadingType !== LOADING_TYPE.NONE}
        />
    );

    const discardButton = (
        <UI.Button
            label={t("labels.discard")}
            disabled={
                !invoiceStore.currentInvoice?.hasChanges || invoiceStore.loadingType === LOADING_TYPE.SAVING_INVOICE
            }
            onClick={onResetForm}
            className={screenSize <= SCREEN_SIZE.MOBILE_L ? `${styles.DiscardButtonMobile} mx-2` : styles.DiscardButton}
            variant="outline-primary"
        />
    );

    return (
        <div id="invoice-form-container" ref={invoiceFormContainerRef}>
            {saveFormModal}
            {saveInvoiceWithoutDocumentModal}
            <div ref={invoiceFormRef}>
                <InvoiceFormDetails />
            </div>

            {invoiceStore.isEditing ? (
                screenSize < SCREEN_SIZE.TABLET ? (
                    <UI.Row
                        className={`d-flex flex-column ${styles.InvoiceFormSaveAndDiscardRowEdit} ${
                            screenSize <= SCREEN_SIZE.MOBILE_L ? "py-3" : "row mt-2"
                        }`}
                    >
                        <UI.Col
                            xs={12}
                            className={`d-flex justify-content-center align-items-center ${
                                screenSize <= SCREEN_SIZE.MOBILE_L ? "" : "mt-2 col-12"
                            }`}
                        >
                            {saveButton}
                        </UI.Col>
                        <UI.Col xs={12} className="d-flex mt-2 justify-content-center align-items-center col-12">
                            {discardButton}
                        </UI.Col>
                    </UI.Row>
                ) : (
                    displaySaveAndDiscardButtonRow && (
                        <UI.Row className="my-2" id="invoice-form-save-and-discard-row">
                            <UI.Col className={"flex-grow-0"}>{saveButton}</UI.Col>
                            <UI.Col>{discardButton}</UI.Col>
                        </UI.Row>
                    )
                )
            ) : undefined}
        </div>
    );
};

export const InvoiceForm = Sentry.withProfiler(observer(InvoiceFormBase));
